import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { metaTrack } from "@/lib/metaPixel";

import { I18nProvider } from "@/context/I18nContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import RestartDating from "@/pages/RestartDating";
import AuthCallback from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import CategoryPage from "@/pages/CategoryPage";
import SmartServices from "@/pages/SmartServices";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Cart from "@/pages/Cart";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import GuestDownload from "@/pages/GuestDownload";
import CvSmartPremium from "@/pages/CvSmartPremium";
import SocialFlyerCreator from "@/pages/SocialFlyerCreator";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Invia Meta Pixel PageView ad ogni cambio pagina, solo con consenso marketing.
const MetaPageView = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { metaTrack("PageView"); }, [pathname]);
  return null;
};

const Shell = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App flex flex-col min-h-screen bg-background">
      <ScrollToTop />
      <MetaPageView />
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/prodotti" element={<Products />} />
          <Route path="/prodotti/:id" element={<ProductDetail />} />
          <Route path="/categoria/:slug" element={<CategoryPage />} />
          <Route path="/percorsi/restart-dating-45" element={<RestartDating />} />
          <Route path="/servizi-smart" element={<SmartServices />} />
          <Route path="/social-flyer-creator" element={<SocialFlyerCreator />} />
          <Route path="/servizi-smart/cv-smart-premium" element={<ProtectedRoute><CvSmartPremium /></ProtectedRoute>} />
          <Route path="/chi-siamo" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contatti" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/carrello" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registrazione" element={<Register />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/guest/download" element={<GuestDownload />} />
          <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
          <Route path="/privacy-policy" element={<LegalPage docKey="privacy" />} />
          <Route path="/cookie-policy" element={<LegalPage docKey="cookie" />} />
          <Route path="/termini" element={<LegalPage docKey="terms" />} />
          <Route path="/recesso-e-rimborsi" element={<LegalPage docKey="refund" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
};

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Shell />
            <Toaster position="bottom-right" richColors />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
